// i18n — English strings (source of truth).
//
// Convention: <surface>.<element>.<state>
// Layer 7 (docs/ui/07-voice.md) is the spec authority for every string.
// This file IS the runtime source of truth — if a string here differs
// from the spec, this file wins and the spec must be updated.
//
// DO NOT add a key here without a parallel key in es.ts. The TypeScript
// type system enforces key parity at compile time (see index.ts).

const en = {
  // ── Product ────────────────────────────────────────────────────────
  product: {
    name: 'As Oasis',
    tagline: 'AI coding IDE for founders',
  },

  // ── Status verbs (Layer 7 D70) ─────────────────────────────────────
  status: {
    running: 'Working',
    waiting: 'Waiting',
    finished: 'Done',
    error: 'Failed',
    interrupted: 'Stopped',
    stuck: 'Stuck',
    archived: 'Archived',
    // Atom-level idle aria-label for `StatusIcon`. After sprint
    // `sidebar-status-icons-binary` (2026-05-12) this is the only
    // idle copy used anywhere — the sidebar-level `agentList.idle`
    // badge ('Idle . Send a message') was removed when the
    // CircleCheck icon took over the "ready for input" affordance.
    idle: 'Idle',
    needsYou: 'Needs you',
    unconfirmed: 'Unconfirmed',
  },

  // ── Topbar ─────────────────────────────────────────────────────────
  topbar: {
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    signOut: 'Sign out',
    notifications: 'Notifications',
    noNotifications: 'No notifications',
    // Sprint UI-8 (A5) — surfaced inside the Bell popover when the
    // founder has at least one unread agent. Plural-aware strings live
    // in the i18n layer; English collapses to a single template that
    // formats fine for 1 or N.
    unreadAgents: '{count} unread agents',
    settings: 'Settings',
    accountMenu: 'Account menu',
    theme: 'Theme',
  },

  // ── Connection status ──────────────────────────────────────────────
  connection: {
    connected: 'Connected',
    reconnecting: 'Reconnecting',
    disconnected: 'Disconnected — tap to retry',
  },

  // Sprint runner-picker-on-create — modal that opens on "+" so the
  // founder can choose which runner the new agent lands on, with
  // remaining-capacity visible at choice time.
  runnerPicker: {
    title: 'Choose a runner',
    subtitle: 'New agents land on the runner you pick.',
    autoLabel: 'Auto (recommended)',
    autoHelper: 'Let As Oasis pick the runner with the most capacity left.',
    capacityFree: '{percent}% free',
    capacityUnknown: 'Capacity unavailable',
    // Sprint runner-picker-session-primary — subtitle line under the
    // session-5h "% free" headline. Built by joining the bucket parts
    // with " · ". Same unit ("free") as the primary so the founder
    // does not need to mentally switch between "free" and "used".
    bucketWeekly: 'Weekly {percent}% free',
    bucketSonnet: 'Sonnet {percent}% free',
    // Sprint usage-limits-v2 (2026-07-08) — subtitle segment when the
    // canonical `limits[]` array surfaces a model-scoped weekly limit.
    // Legacy sonnet bucket keeps its own key; new scoped kinds render
    // "Weekly · <model>" with per-model free %.
    bucketWeeklyScoped: 'Weekly · scoped {percent}% free',
    bucketWeeklyScopedModel: 'Weekly · {model} {percent}% free',
    offlineReason: 'Offline — pick another runner.',
    drainingReason: 'Draining — accepting no new agents.',
    // Sprint runner-quarantine-truth (D5, D013-EC-127) — quarantined
    // runners stay selectable: pinning is the documented founder
    // override of quarantine, so the caption makes the override an
    // informed decision instead of a silent bypass.
    quarantinedCaption: 'Quarantined — you can still pin it.',
    confirm: 'Create agent',
    cancel: 'Cancel',
  },

  // ── Sidebar ────────────────────────────────────────────────────────
  sidebar: {
    newAgent: 'New Agent',
    newAgentDisabled: 'New agents paused — server low on memory',
    search: 'Search agents',
    // Sprint UI-8 (A1) — aria-label for the 4 px drag handle that resizes the
    // sidebar. Pre-existing string, lifted into i18n now that the surrounding
    // file passes through lint after the Topbar removal.
    resizeAriaLabel: 'Resize sidebar',
    empty: {
      title: 'No agents yet',
      subtitle: 'Start your first one',
    },
    showMore: 'Show {count} more',
    // Sprint agent-stages — the recency sections (PINNED/TODAY/YESTERDAY/
    // OLDER) were retired; sections are now the founder's workflow stages
    // (`stages` group below). ARCHIVED stays.
    section: {
      archived: 'ARCHIVED',
    },
    project: {
      label: 'Project',
      all: 'All projects',
    },
  },

  // ── Workflow stages (sprint agent-stages) ───────────────────────────
  stages: {
    noStage: 'No stage',
    moveTo: 'Move to stage',
    newStage: 'New stage…',
    manage: 'Manage stages…',
    manageTitle: 'Stages',
    createTitle: 'New stage',
    editTitle: 'Edit stage',
    newStageButton: 'New stage',
    emptyList: 'No stages yet. Create the first one.',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. Soak',
    colorLabel: 'Color',
    colorOption: 'Color {n}',
    create: 'Create',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    rename: 'Edit',
    delete: 'Delete',
    moveUp: 'Move up',
    moveDown: 'Move down',
    filterAll: 'All',
    deleteConfirmTitle: 'Delete stage?',
    deleteConfirmBody:
      'The {count} agents in "{name}" will move to "No stage". No agents are deleted.',
    createError: 'Could not create the stage',
    createNeedsWorkspace: 'Pick a workspace first — stages belong to a project',
    updateError: 'Could not update the stage',
    deleteError: 'Could not delete the stage',
  },

  // ── Workspace column ────────────────────────────────────────────────
  workspace: {
    addProject: 'New project',
    newProject: 'New project',
    repoUrl: 'Repo URL',
    slugInvalid: 'Slug must start with a letter or number and contain only lowercase letters, numbers, dots, hyphens.',
    cloning: 'Cloning…',
    creating: 'Creating…',
    create: 'Create',
    done: 'Done',
    namePlaceholder: 'My Project',
    slugPlaceholder: 'my-project',
    repoPlaceholder: 'git@github.com:org/repo.git',
    branchPlaceholder: 'main',
  },

  // ── Workspace customization (Sprint 11b.2) ─────────────────────────
  workspaceCustomization: {
    edit: 'Edit project…',
    archive: 'Archive',
    editTitle: 'Edit project',
    // Sprint add-project-autoprovision — repair primitive for wrong
    // repo URLs (saving re-runs project setup automatically; an
    // unchanged re-save is the explicit retry gesture).
    repoUrl: 'Repository URL',
    repoUrlHint: 'Saving re-runs project setup on the runners.',
    repoUrlPlaceholder: 'https://github.com/owner/repo',
    repoUrlError: 'Could not update the repository URL. Try again.',
    initials: 'Initials',
    initialsHint: 'Exactly 2 letters or numbers, uppercase.',
    color: 'Color',
    autoColor: 'Auto color',
    autoGlyph: 'A',
    colorOption: 'Color {n}',
    resetAuto: 'Reset to auto',
    save: 'Save',
    saving: 'Saving…',
    reorderError: 'Couldn’t reorder — try again.',
    updateError: 'Couldn’t update — try again.',
    syncIncomplete: 'Sync incomplete on {count} server(s).',
  },

  // ── Agent context menu ─────────────────────────────────────────────
  contextMenu: {
    markRead: 'Mark read',
    markUnread: 'Mark unread',
    rename: 'Rename',
    fork: 'Fork',
    archive: 'Archive',
    restore: 'Restore',
    delete: 'Delete',
    ariaLabel: 'Agent actions',
  },

  // Sprint move-runner-button (EC-135) — proactive move from the ⋯ menu.
  moveRunner: {
    menuItem: 'Move to another server',
    pickerTitle: 'Move to another server',
    pickerConfirm: 'Move here',
    confirmTitle: 'Move this conversation to another server?',
    confirmBody:
      'The full conversation memory travels. Unsaved changes get packaged into a wip-move branch, pushed to GitHub, and the agent recovers them on the new server.',
    confirmConfirm: 'Move',
    successToast: 'Conversation moved to {hostname}.',
    errorToast: "Couldn't move: {reason}",
  },

  // ── Chat header ────────────────────────────────────────────────────
  chatHeader: {
    renameTooltipDesktop: 'Double-click to rename',
    renameTooltipMobile: 'Tap to rename',
    moreActions: 'More actions',
    backAriaLabel: 'Back to agent list',
    // Sprint fc-conversation-search (§2.2) — magnifier trigger, immediately
    // left of the ⋯ menu. Same string doubles as the disabled tooltip's
    // enabled-state fallback; the disabled reason itself comes from
    // conversationSearch.loading / .loadError (Gate 2 fix round item 9).
    search: 'Search this conversation',
  },

  // ── Chat — empty state ─────────────────────────────────────────────
  chat: {
    empty: {
      title: 'Pick an agent or start a new one',
      subtitle: 'Select an agent from the sidebar to view its messages, send a follow-up, or stop the run.',
      openSidebar: 'Show agent list',
    },
    // Sprint S20.5 PR-4 (D013-EC-70) — chat-header pill for the
    // `waiting` status. Mirrors the sidebar badge but with a slightly
    // longer hint because the chat header has more horizontal room and
    // is the founder's primary surface for the selected agent.
    idleHint: 'Idle · Send a message to continue.',
  },

  // ── Composer ────────────────────────────────────────────────────────
  composer: {
    heading: 'New agent',
    openSidebar: 'Open sidebar',
    placeholder: {
      new: 'What should this agent do?',
      continue: 'Send a message to continue this agent...',
      waiting: 'Send a message · / for commands · @ for mentions',
      running: 'Add a follow-up…',
      stuck: 'Send a message — the agent will pick it up.',
      finished: 'Send a message to continue this agent…',
      interrupted: 'Send a message to continue this agent…',
      errorRecoverable: 'Send a message to continue (will retry)…',
      idle: 'Send a message…',
      working: 'Add a follow-up…',
      disconnected: 'Will send when reconnected',
      archived: 'This agent is archived. Restore it to continue.',
      permissionPending: 'Waiting for your permission decision above',
      default: 'Send a message…',
      exampleHint: 'Read the README and summarise it in 5 bullets...  ·  type / for commands, @ for mentions',
    },
    hint: {
      running: 'Waiting for the agent to finish the current turn…',
      stuck: 'No activity for several minutes — the process is alive but may be wedged.',
      finished: 'Agent finished — send to continue or fork to branch.',
      interrupted: 'Agent was stopped — send to resume or fork to branch.',
      error: 'Agent failed — send to retry, or fork from the last working turn.',
    },
    send: 'Send',
    sending: 'Sending…',
    starting: 'Starting…',
    stop: 'Stop',
    stopping: 'Stopping…',
    stopAriaLabel: 'Stop agent',
    sendAriaLabel: 'Send message',
    attachAriaLabel: 'Attach file',
    promptLabel: 'Prompt',
    promptTooLong: 'Prompt too long — remove {count} chars to send.',
    promptApproachingLimit: 'Approaching the prompt limit.',
    enterToSend: 'Enter to send · Shift + Enter for new line',
    memoryWarning: 'Server memory is low — new agents are paused until the running ones free up RAM.',
    couldNotStart: 'Could not start agent.',
    couldNotChangeAgent: 'Could not change agent',
    sendFailed: 'Message not sent. Tap to retry.',
    attachmentLost: 'An attachment was lost. Re-attach it and send again.',
    attachmentsLost: 'Some attachments were lost. Re-attach them and send again.',
    // Sprint attachments-anthropic-parity Phase D — accessible labels for
    // the image thumbnail + fullscreen lightbox (D7, D8, D9).
    imageLightboxOpen: 'View full image',
    imageLightboxClose: 'Close image',
    removeAttachmentAriaLabel: 'Remove attachment',
    retryAttachmentAriaLabel: 'Retry attachment',
    // Sprint work-segment-collapse (D013-EC-526, Gate 2 minor issue 4) —
    // the failed-thumbnail frame (AttachmentChip.tsx) is a disabled
    // button with no visible text; this is both its aria-label and its
    // title, so the file name (not shown once the thumbnail fails) stays
    // reachable via the title. Size is deliberately not included —
    // founder-first-vision.md §6 keeps cost-like detail out of the
    // founder layer.
    imageUnavailable: 'Image could not be loaded',
    confirmModeSwitch: {
      toPlan: 'Switch to Plan mode?',
      toAgent: 'Switch to Agent mode?',
      body: 'An action is waiting for your approval. Switching modes will cancel it.',
      confirmPlan: 'Switch to Plan',
      confirmAgent: 'Switch to Agent',
    },
  },

  // ── Confirm dialogs ────────────────────────────────────────────────
  confirm: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    working: 'Working…',
    stopAgent: {
      title: 'Stop this agent?',
      body: 'The current Claude process will be killed. The chat history stays in the archive.',
      confirm: 'Stop',
    },
    forkAgent: {
      title: 'Fork this agent?',
      body: 'A new agent resumes the same Claude session. Both agents share history up to this point and diverge afterward.',
      confirm: 'Fork',
    },
    deleteAgent: {
      title: 'Delete this agent?',
      body: 'The process is killed and the agent disappears from the active list. Cannot be undone.',
      confirm: 'Delete',
    },
    deleteAgentSidebar: {
      title: 'Delete agent?',
      bodyTemplate: '"{name}" will be stopped and removed. You have 5 seconds to undo.',
      confirm: 'Delete',
    },
    signOut: {
      title: 'Sign out?',
      body: "You'll need to sign in again to access the dashboard.",
      confirm: 'Sign out',
    },
    resetSettings: {
      title: 'Reset all settings?',
      body: 'This restores defaults. Cannot be undone.',
      confirm: 'Reset',
    },
    changeMode: {
      title: 'Switch to {mode} mode?',
      body: 'The agent will respawn under the new mode. Chat history stays; the running Claude process restarts.',
      confirm: 'Switch',
    },
  },

  // ── Toasts ─────────────────────────────────────────────────────────
  toast: {
    undo: 'Undo',
    dismiss: 'Dismiss notification',
    agentArchived: 'Agent archived',
    agentRestored: 'Agent restored',
    agentDeleted: 'Agent deleted',
    agentStopped: 'Agent stopped',
    undoFailed: 'Undo failed — agent will be purged in a few seconds',
    ariaLabel: 'Notifications',
    couldNotPin: 'Could not pin agent. Try again.',
    couldNotArchive: 'Could not archive agent. Try again.',
    couldNotRestore: 'Could not restore agent. Try again.',
    couldNotDelete: 'Could not delete agent. Try again.',
    couldNotRename: 'Could not rename agent. Try again.',
    couldNotSave: 'Could not save. Try again.',
    retry: 'Retry',
    // Sprint 20.3 PR-10 (D013-EC-43) — scheduler refused the spawn
    // because every runner is offline / drained / capped. Action label
    // for the spawn-error toast that points at the centralized usage
    // modal. Founder copy for the toast itself moved to
    // `errors.spawn.NO_RUNNER_AVAILABLE` in S20.5 PR-3 so every spawn
    // branch is owned by the same namespace.
    viewRunnerUsage: 'View runner usage',
  },

  // ── Login page ─────────────────────────────────────────────────────
  login: {
    passwordLabel: 'Password',
    passwordEmpty: 'Enter your password.',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    couldNotSignIn: 'Could not sign in. Try again.',
    wrongPassword: 'Wrong password',
    tooManyAttempts: 'Too many attempts — try again in {seconds}s',
  },

  // ── Access gate re-entry screen (sprint access-session-recovery, D5) ─
  // Own namespace, not `login.*`: this is a DIFFERENT layer (the identity
  // gate in front of the app, D6) than the password form, and a different
  // meaning than `SessionExpiredBanner`'s "session expired" (that one is a
  // Claude runner session, not the security gate). Copy stays free of
  // "Cloudflare", "JWT", "cookie", "service worker", "302" per D5 — it
  // names the CAUSE ("your security session"), not the mechanism.
  accessGate: {
    title: 'Your security session has expired',
    message: 'Sign in again to keep using As Oasis.',
    reenterButton: 'Sign in again',
    // Challenger R1 change 5 — EC-3 self-report: `?reauth=` is present in
    // the URL AND we're still `gate-expired` after boot, meaning the
    // round trip through sign-in did not leave a session behind.
    stillBlockedMessage: "That didn't finish. Try signing in again.",
    // Challenger R1 change 12 — engine defence. If the browser never
    // produces an `opaqueredirect` (observed on WebKit), a real Access
    // rejection classifies as `NETWORK_ERROR` and the password form stays
    // up. This secondary, non-automatic affordance does the same D3
    // navigation without needing the classifier to say "gate".
    networkErrorHint: "Can't sign in? The password may not be the problem.",
    networkErrorButton: 'Try signing in again',
    // DocViewerSheet — same gate, encountered while a doc overlay is open.
    docViewerBlocked: 'Your security session has expired. Reload the page to continue.',
  },

  // ── Notification feed (Sprint notification-center) ─────────────────
  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all read',
    openAction: 'View',
  },

  // ── Settings page ──────────────────────────────────────────────────
  settings: {
    heading: 'Settings',
    backToChat: '← Back to chat',
    display: {
      title: 'Display',
      description: 'What you see in the chat by default.',
      developerMode: 'Show developer details',
      developerModeDescription: 'Reveal raw tool calls, diffs, bash commands, and JSON inputs inline. When off, every tool call gets a humanized one-line summary; you can still expand individual ones with the ▽.',
    },
    conversations: {
      title: 'Conversations',
      description: 'What the system does when a server is running low on room.',
      autoFailover: 'Move conversations automatically',
      autoFailoverDescription: 'When a conversation\'s server is running low on room, the system moves it to another server on its own and keeps working. Turn this off if you\'d rather move it yourself with the conversation\'s button.',
    },
    notifications: {
      title: 'Notifications',
      description: 'How you find out about agents you\'re not watching live.',
      tierA: 'Needs your action',
      tierADescription: 'Approvals and questions. Always shows a toast with sound in the app; this switch also sends a push to your devices.',
      tierB: 'Informational',
      tierBDescription: 'An agent finishes, errors, or gets stuck. Appears in the bell feed with a badge — no push, no sound.',
      tierC: 'System problems',
      tierCDescription: 'Something is broken (disk, a server down). Always reaches you via Telegram and push — this one can\'t be turned off.',
      sound: 'Notification sound',
      soundDescription: 'Plays a short chime when an agent needs your action — even while you\'re watching the dashboard.',
      pushDevice: 'Push on this device',
      pushDeviceDescription: 'Get notifications on this device even with the app closed.',
      pushEnable: 'Enable',
      pushDisable: 'Disable',
      pushUnsupported: 'This browser can\'t receive push. On iPhone, install the app first: Share → Add to Home Screen.',
      pushDenied: 'Notifications are blocked for this app. Allow them in your device settings and come back.',
      pushFailServerDisabled: 'Notifications aren\'t activated on the server yet. They\'ll turn on with the next deploy — try again after that.',
      pushFailPermissionDenied: 'You didn\'t allow notifications. Tap Enable again and choose Allow.',
      pushFailNoRegistration: 'This app isn\'t fully installed on this device. On iPhone: Share → Add to Home Screen, then open it from there.',
      pushFailError: 'Couldn\'t turn on push on this device. Try again in a moment.',
      testSend: 'Send test notification',
      testSending: 'Sending…',
      testDisabledHint: 'Turn on push for this device first, then send a test.',
      testSent: 'Sent — check your device.',
      testRateLimited: 'Wait a few seconds before sending another.',
      testFailed: 'Could not send the test. Try again.',
      telegram: 'Send notifications to Telegram',
      telegramDescription: 'Mirror agent updates to your Telegram group. System problems always go to Telegram regardless of this switch.',
    },
    permissions: {
      title: 'Default permission mode',
      description: 'How new agents handle tool execution by default. You can override per-agent with the mode chip.',
    },
    allowlist: {
      title: 'Allowed forever',
      description: 'Things you\'ve told Claude it can do without asking again. Forget any of them and the agent will ask the next time.',
      loading: 'Loading…',
      couldNotLoad: 'Could not load saved permissions',
      empty: 'You haven\'t allowed anything forever yet. Click "Allow always for…" on a permission card to save a rule here.',
      forget: 'Forget',
      forgetting: 'Forgetting…',
      couldNotForget: 'Could not forget this rule',
      showAll: 'Show all ({count})',
      toolKind: {
        Bash: 'Run command',
        Read: 'Read file',
        Edit: 'Edit file',
        Write: 'Write file',
        MultiEdit: 'Edit file',
        NotebookEdit: 'Edit notebook',
        Glob: 'Find files',
        Grep: 'Search code',
        WebFetch: 'Fetch URL',
      },
    },
    language: {
      title: 'Language',
      description: 'Choose the display language for the dashboard.',
      english: 'English',
      spanish: 'Español',
    },
    account: {
      title: 'Account',
      description: 'Your account settings.',
    },
    servers: {
      title: 'Servers',
      description: 'VPS instances running As Oasis agents.',
      addServer: 'Add server',
      name: 'Name',
      baseUrl: 'Base URL',
      id: 'Server ID',
      online: 'Online',
      offline: 'Offline',
      remove: 'Remove',
      save: 'Save',
      cancel: 'Cancel',
      empty: 'No servers configured yet. Add a server to start managing agents across multiple VPS.',
      namePlaceholder: 'VPS 1',
      baseUrlPlaceholder: 'https://vps1.asoasis.com',
      idPlaceholder: 'vps1',
    },
    projects: {
      title: 'Projects',
      description: 'Code repositories managed by agents.',
      addProject: 'Add project',
      name: 'Name',
      slug: 'Slug',
      workdir: 'Working directory',
      defaultBranch: 'Default branch',
      archive: 'Archive',
      unarchive: 'Restore',
      empty: 'No projects yet. Projects are created automatically when agents start.',
      save: 'Save',
      cancel: 'Cancel',
    },
    // Sprint 20.3 PR-8 — admin runners panel.
    runners: {
      title: 'Runners',
      description: 'Servers that host the Claude CLI for agents.',
      empty: 'No runners registered yet.',
      loadError: 'Could not load runners.',
      refresh: 'Refresh',
      reload: 'Refresh runners',
      // Column labels
      colHostname: 'Hostname',
      colStatus: 'Status',
      colAccount: 'Account',
      colCap: '5h used',
      colWeeklySonnet: 'Sonnet · this week',
      colWeeklyOpus: 'Opus · this week',
      colActive: 'Active',
      // Sprint fleet-resource-metrics (spec §2 D10) — per-runner
      // resource telemetry cells + sparkline trend label.
      colCpu: 'CPU',
      colRam: 'RAM',
      colDisk: 'Disk',
      colTrend: 'Trend (6h)',
      // Sprint fleet-resource-metrics (spec §2 D10) — sparkline empty
      // state + accessible label (i18n-enforce: no literal JSX strings).
      trendEmpty: '—',
      trendAriaLabel: 'CPU, RAM and disk usage trend',
      colLastSeen: 'Last seen',
      colVersion: 'Version',
      colActions: 'Actions',
      // Sprint runner-checkout-freshness (ADR D-1073, VISIBILITY half)
      colCheckout: 'Checkout',
      checkoutSummary: '{commits} behind · {repo}',
      checkoutStale: '(stale)',
      // Status badges
      statusOnline: 'Online',
      statusOffline: 'Offline',
      statusDraining: 'Draining',
      statusIncompatible: 'Incompatible version',
      // Actions
      drain: 'Drain',
      undrain: 'Undrain',
      reEnable: 'Re-enable',
      // Confirm modal
      confirmDrainTitle: 'Drain {hostname}?',
      confirmDrainBody: 'Drain stops new agents from being scheduled to {hostname} and waits for in-flight agents to finish before the runner exits cleanly.',
      confirmDrain: 'Drain runner',
      // Per-row banner when unavailable_reason is set
      unavailableReasonBlocked: 'Claude account blocked. Resolve with Anthropic, then re-enable.',
      unavailableReasonDiskFull: 'Disk full. Reclaim space on the runner, then re-enable.',
      unavailableReasonNotAuthed: 'Claude not authenticated. Run `claude login` on the runner, then re-enable.',
      unavailableReasonIncompatible: 'Runner is on an incompatible protocol version. Deploy the matching runner build, then re-enable.',
      // Sprint runner-resilience-after-missing-credential §2.4.
      unavailableReasonMissingCredential: 'A 1Password credential failed the runner’s boot healthcheck. Check the runner log for failing item titles and resolve via OFM. The runner auto-recovers within ~10 min once the issue is fixed.',
      // Sprint runner-op-token-single-source (defect 3) — the runner’s OP
      // token authenticates to the wrong 1Password workspace.
      unavailableReasonWrongWorkspace: 'The runner’s 1Password token authenticates to the wrong workspace. Expected santi-team.1password.com. Operator must rotate /etc/asoasis/op-token via the OFM resync mechanism (see runbook).',
      // Toasts
      drainSuccess: '{hostname} is draining.',
      drainError: 'Could not drain {hostname}: {reason}',
      undrainSuccess: '{hostname} is back in rotation.',
      undrainError: 'Could not undrain {hostname}: {reason}',
      clearUnavailableSuccess: '{hostname} re-enabled.',
      clearUnavailableError: 'Could not re-enable {hostname}: {reason}',
      // Misc
      capUnknown: '—',
      lastSeenNever: 'never',
      hours: '{value}h',
      hoursOfLimit: '{value}h / {limit}h',
      activeAgents: '{count}',
    },
  },

  // ── System pills ───────────────────────────────────────────────────
  system: {
    agentStarted: 'Agent started',
    newTurn: 'New turn',
    turnComplete: 'Turn complete',
    turnCompleteWithDuration: 'Turn complete · {duration}',
    agentExited: 'Agent {status}',
    switchedTo: 'Switched to {model} · {effort}',
    configChanged: 'Configuration changed',
    stopped: 'Stopped',
    agentCrashed: 'Agent crashed',
    reopenedConversation: 'Reopened conversation',
    readyContinuing: 'Ready — continuing',
    // Sprint context-limit-survivable (Seam C) — FALLBACKS ONLY. The backend
    // writes its own sentence on every one of these rows and the pill renders
    // that verbatim; these fire only when a row carries none. Plain language,
    // a next action where there is one, no jargon (founder-first §5) — no
    // "context", "token", "transcript", "runner".
    conversationCompacted: 'The conversation was shortened so it can carry on',
    messageNotDelivered: "That message didn't get through — write it again",
    // Sprint lazy-spawn-recovery (§2 D11) — FALLBACKS ONLY, same pattern
    // as the two above. The backend authors the real sentence on every
    // row (`renderFailoverFounderCopy` / the recovery pill message).
    reassigned: 'Moved to another server',
    recoveredNeverStarted: 'Recovered this conversation',
    // Founder-first §5 — this is the ONLY string the founder gets when an
    // `error` row carries neither a known `code` nor a `message`, i.e. the
    // exact case the "always name a next action" rule exists for. The one
    // action that is available with no idea what failed: open a new one.
    error:
      "Something went wrong and this conversation can't continue — open a new conversation to keep going",
    // Sprint context-limit-survivable (PR 1, M9 copy pass) — NOT fallbacks.
    // These three are the copy the pill renders for the three runner
    // `agent.error` codes, resolved from the row's machine-readable `code`
    // instead of its persisted `message`, so the rows written before the
    // copy pass (backend English carrying "Runner" / "session") are healed
    // on the next reload rather than surfacing loud and unreadable. Twins of
    // `FOUNDER_ERROR_COPY` in `backend/src/claude/session-manager.ts` — keep
    // in step. One next action each; the three meanings are genuinely
    // different, so the copy is too.
    errorSessionLost:
      'This conversation broke off. Write a message here to pick it up again.',
    errorSessionExpired:
      "This conversation expired and can't continue. Everything written stays here; open a new conversation to keep going.",
    errorRestartLoop:
      'This conversation kept restarting in a loop, so we stopped it. Open a new conversation to continue.',
    // Sprint stdout-transport-replay (D013-EC-118) — loud, visible
    // copy for verified transport loss / truncation. No jargon (no
    // "WebSocket", no "seq"): plain cause + what to do next.
    outputLost:
      'Part of the agent’s reply was lost due to a connection drop — ask the agent to repeat its last answer',
    outputTruncated:
      'A message from the agent was too large to deliver — ask the agent to resend it in smaller parts',
    // Sprint compaction-visibility (D1) — FALLBACK only; the backend
    // writes its own sentence (`message`, Spanish per the founder-language
    // invariant) on every `compaction_failed` row and the pill renders it
    // verbatim (`persistedMessage()`). Twin of
    // `COMPACTION_FAILED_FOUNDER_MESSAGE` in
    // `backend/src/claude/session-manager.ts` — keep in step. Deliberately
    // NOT translated to English here: this string is the fallback for the
    // SAME founder-facing Spanish sentence, not a locale-native one.
    compactionFailed: 'No se pudo compactar la conversación.',
  },

  // ── Error card ─────────────────────────────────────────────────────
  error: {
    agentFailed: 'Agent failed',
    agentFailedWithReason: 'Agent failed: {reason}',
  },

  // ── Spawn errors (Sprint S20.5 PR-3 / D013-EC-69) ─────────────────
  // Founder-mode copy for the toast that fires when `POST /api/agents`
  // (or the lazy first-message spawn) is rejected by the backend with
  // a structured `code`. Each branch tells the founder what to DO next
  // in plain language; the generic fallback is the legacy string and
  // is preserved here for unrecognised codes.
  errors: {
    spawn: {
      NO_RUNNER_AVAILABLE:
        'No servers available. Open Settings \u2192 Servers to see what\u2019s offline.',
      RUNNER_NOT_READY:
        'The server is starting up. Try again in a few seconds.',
      CLAUDE_NOT_AUTHENTICATED:
        'Claude isn\u2019t signed in on this server. Ask the operator to run `claude /login` on the server.',
      RUNNER_OFFLINE:
        'The server for this agent is offline. Try again or pick another server.',
      GENERIC: 'Could not create the conversation. Tap to retry.',
    },
  },

  // ── Agent state banners ──────────────────────────────────────────
  agentBanner: {
    stuckTitle: 'No activity in {duration}',
    stuckWait: 'Wait',
    stuckStop: 'Stop',
    errorTitle: 'Agent stopped after {reason}',
    errorRetry: 'Retry',
    errorFork: 'Fork from last working turn',
    interruptedTitle: 'Stopped after {duration}',
    interruptedResume: 'Resume',
    interruptedFork: 'Fork',
  },

  // ── Network / system banners ─────────────────────────────────────
  networkBanner: {
    reconnecting: 'Reconnecting',
    disconnected: 'Disconnected — agents keep running on the server',
    disconnectedRetry: 'Retry',
  },

  // ── Spawn preparing toast (Sprint 20.3 PR-5) ─────────────────────
  // Founder-mode auto-clone surface. Displayed below the chat when
  // the runner is preparing the project clone before the first
  // `agent.spawn_request`. Failure copy is owned by the backend
  // (`founderCopyForCloneFailure()` in session-manager.ts) so the
  // backend `errorMessage` field on the agent matches the toast.
  spawnPreparingToast: {
    failedTitle: 'Project setup failed',
    retry: 'Retry',
    dismissAria: 'Dismiss notification',
    dismissGlyph: '×',
  },

  // ── Runner-unavailable failover banner ────────────────────────────
  // S20.3 PR-6 introduced the offline variant; auto-failover-on-rate-limit
  // (D013-EC-109) extends with the rate-limited variant. Same banner
  // pattern, two discriminated copies. Founder-mode: plain language —
  // NEVER "rate-limit" or "weekly cap" in the user-visible surface.
  runnerUnavailableBanner: {
    // Per-variant copy.
    offline: {
      title: '{runner} is offline',
      subtitle: "Your agent's runner is unreachable. Try another runner to keep going.",
      tryAnotherRunner: 'Try another runner',
    },
    rateLimited: {
      title: '{runner} reached its weekly quota',
      subtitle:
        "Your agent's runner has no weekly quota left. Move this conversation to another runner to keep going.",
      tryAnotherRunner: 'Move to another runner',
    },
    // Shared affordance copy.
    busyLabel: 'Reassigning…',
    dismissAria: 'Dismiss',
    dismissGlyph: '×',
    successToast: 'Reassigned to {to}.',
    errorToast: 'Failover failed: {reason}',
    genericError: 'Failover failed.',
  },

  // Sprint 20.5 PR-2 (D013-EC-68).
  sessionLostBanner: {
    title: 'This session ended',
    subtitle: 'Tap retry to resume the conversation.',
    retry: 'Retry',
    busyLabel: 'Resuming…',
    dismissAria: 'Dismiss session-ended banner',
    dismissGlyph: '×',
    successToast: 'Conversation resumed.',
    errorToast: 'Could not resume: {reason}',
    genericError: 'Could not resume.',
  },

  // Sprint 20.6 PR-2 (D013-EC-72) — Anthropic dropped the resumed
  // sessionId. Resume is impossible by definition; the next-best
  // founder action is a fresh agent.
  sessionExpiredBanner: {
    title: 'This session expired',
    subtitle: 'Anthropic dropped the conversation context. Start a new agent to keep going.',
    newAgent: 'Start a new agent',
    dismissAria: 'Dismiss session-expired banner',
    dismissGlyph: '×',
  },

  // Sprint 20.6 PR-2 (D013-EC-72) — non-blocking 2 s toast that lets
  // the user know the parpadeo they just saw was a deliberate respawn,
  // not a crash.
  respawnedToast: {
    message: 'Conversation resumed',
  },

  // ── Live activity (Sprint 17) ──────────────────────────────────────
  // Single in-flight indicator below the last message. Surface for the
  // founder is intentionally short: actor + verb + state. The headline
  // for `working` is the same `tool_use.input.description` (or verb-map
  // fallback) the persisted ToolCallRow shows.
  liveActivity: {
    thinking: 'Thinking',
    working: 'Working',
    // Sprint compaction-visibility (D4/D13) — same container as
    // 'thinking': pulsing dots + elapsed time.
    compacting: 'Compacting context…',
    ariaLabel: 'Agent activity',
    // Sprint d028-recursive-liveness PR-C (D5, EC-176) — prefix for the
    // 'tool' row when the activity belongs to a subagent (child), not the
    // conversation itself. `{label}` is the same `tool_use.input.description`
    // fallback chain the parent's own row uses — no NEW fallback chain.
    // `{label}` is tool-name-free by construction since D013-EC-214
    // (PR #581): `fallbackHeadline`'s default returns per-kind founder
    // copy or 'Used a tool', never the title-cased identifier.
    subagentLabel: 'Subagent: {label}',
  },

  // ── Sprint working-signal-ssot — task-scoped working captions ──────
  // Rendered by the sidebar subtitle and the chat fallback activity
  // line when the agent is working but LiveActivity is silent (open
  // permission card, background subagent, scheduled wakeup, silent
  // long turn).
  workingReason: {
    working: 'Working…',
    silent: 'Still working — no output for a while',
    awaitingPermission: 'Waiting for your permission',
    awaitingTask: 'Waiting on a background agent',
    awaitingWakeup: 'Waiting for a scheduled check',
    awaitingWakeupAt: 'Waiting for a scheduled check · {time}',
    // Sprint conditional-wakeups PR-A (D7/D8) — when the delivery chain
    // is blocked (attempt_count > 0 AND last_error set), this line
    // REPLACES the generic "waiting..." prefix instead of appending to
    // it (design decision post-truncation-finding: the blocked state is
    // strictly more informative than "waiting for a scheduled check";
    // leading with the anomaly is founder-first §2 priority 4 — "is it
    // blocked or asking something?"). Humanized, never the raw code
    // (founder-first §8.6).
    // PR-B — reviewer note: the trailing time is labelled "scheduled"
    // (not a bare, context-free hour). **TIMED rows only** — there the
    // time IS a scheduled resume hour. A blocked CONDITIONAL row uses
    // `wakeupBlockedLimitAt` (below): M7 forbids labelling the deadline
    // as an expected/scheduled time.
    wakeupBlockedAt: 'Scheduled notice blocked: {reason} · attempt {attempt} · scheduled {time}',
    wakeupBlocked: 'Scheduled notice blocked: {reason} · attempt {attempt}',
    // Sprint conditional-wakeups PR-B Gate-2 fix (M7 BLOCKER) — the
    // blocked CONDITIONAL row gets its own key: the time is
    // `wakeupDeadlineAt`, never `wakeupFireAt`, labelled "limit" (never
    // "scheduled" — that would imply a scheduled resume time, which M7
    // forbids for the conditional channel).
    wakeupBlockedLimitAt: 'Notice blocked: {reason} · attempt {attempt} · limit {time}',
    blockedReason: {
      runnerOffline: 'server unavailable',
      runnerRateLimited: 'weekly quota exhausted',
      capacityExhausted: 'no capacity',
      default: 'retrying',
    },
    // Sprint conditional-wakeups PR-B (spec D8, M7) — conditional row
    // (asoasis-wake). "limit" not "resumes at": the deadline is a limit,
    // never a resume time. `note` is the ONLY probe-related string ever
    // rendered (never probe_cmd, never a raw code).
    awaitingConditionNote: 'waiting: {note} · limit {time}',
  },

  // ── Todo checklist ─────────────────────────────────────────────────
  todos: {
    heading: 'Todos',
    empty: 'TodoWrite (no items)',
    progress: '{completed}/{total} done',
    cancelled: '{count} cancelled',
  },

  // ── Plan mode banner ───────────────────────────────────────────────
  planMode: {
    banner: 'Plan mode — research only, no file changes',
  },

  // ── Subagent panel ─────────────────────────────────────────────────
  subagent: {
    label: 'Subagent',
    tools: '{count} tool',
    toolsPlural: '{count} tools',
    running: '(working…)',
    error: '(failed)',
    prompt: 'Prompt',
    subagentError: 'Subagent error',
    finalResult: 'Final result',
    emptyResult: '(empty)',
    // Sprint subagent-panel-ux (D-sap-3) — founder-mode labels for the
    // prompt sent to the subagent and its response, as readable prose.
    promptFounder: 'It was asked to:',
    resultFounder: 'It returned:',
    // Sprint ai-elements-rendering — recursive subagent tree.
    depthBadge: 'N{depth}',
    descendantTools: '(+{count})',
    orphanNotice: 'Subagent output without its parent call (history truncated)',
    // Sprint subagent-launch-ack-liveness (D4, D013-EC-659) — the fourth card state: no
    // completion signal ever arrived (26% of D013-EC-169), the age bound tripped (B3), or the
    // parent is genuinely idle. Never "finished".
    unconfirmed: '(no completion signal)',
    // Sprint subagent-launch-ack-liveness (D4, item 8, §7b C10) — developer-layer label for
    // the raw launch tool_result of a backgrounded launch, shown alongside (or instead of) the
    // real completion summary — never the founder-facing result body.
    launchAck: 'Launch receipt (raw)',
    // Founder directive 2026-09-13 (mockup approval): aria-label for the green check glyph
    // shown when the subagent has finished without error — the header's `role="status"`
    // equivalent for the settled state.
    done: 'done',
  },

  // ── Dispatched worker card ─────────────────────────────────────────
  // Sprint dispatch-lineage-ui (D4b) — one `/dispatch` this caller made,
  // rendered with the SAME component as a subagent. States map the
  // `dispatches` ledger terminals (owner-dispatch D6b).
  dispatchCard: {
    label: 'Worker',
    working: '(working…)',
    quiet: '(quiet)',
    reporting: '(finished · reporting)',
    reported: '(reported)',
    failed: '(failed)',
    deadline: '(deadline passed)',
    needsFounder: '(needs your approval)',
    noStart: '(did not start)',
    loading: 'Loading the transcript…',
    unknownCaller: 'another agent',
  },

  // ── Workflow panel ─────────────────────────────────────────────────
  // Sprint d028-workflow-agents (D2) — a Workflow launch's N inner agents.
  // Header noun reuses the EXISTING `Workflow` humanisation
  // (verb-map.ts/headline.ts `HARNESS_TOOL_COPY.Workflow`) via this key,
  // never a raw tool identifier (D013-EC-214). `.agents`/`.agentsPlural`
  // pair mirrors the `subagent.tools`/`.toolsPlural` precedent (R3-m7).
  // `.interrupted` is a `FounderRowStatus` member (founder-layer.ts) —
  // the settled-non-done state for a row that never reached `done`
  // (killed/interrupted/rate-limited Workflow, D013-EC-147 class).
  // `.noPreview` covers an element whose per-state preview field
  // (`promptPreview`/`resultPreview`) is absent — never an empty body.
  workflowPanel: {
    header: 'Ran a workflow',
    agents: '{count} agent',
    agentsPlural: '{count} agents',
    running: '(working…)',
    done: '(done)',
    interrupted: '(interrupted)',
    noPreview: 'No details available yet.',
  },

  // ── Dual mode chip ─────────────────────────────────────────────────
  dualMode: {
    label: 'Developer mode',
    disableTooltip: 'Disable developer mode',
  },

  // ── Permissions ────────────────────────────────────────────────────
  permissions: {
    agentWantsTo: '{agent} wants to {action}',
    allowOnce: 'Allow once',
    allowAlways: 'Allow always',
    deny: 'Deny',
    // Bash intents
    deleteFolderNamed: '{agent} wants to delete the folder {name}',
    deleteFolder: '{agent} wants to delete a folder',
    deleteFileNamed: '{agent} wants to delete {name}',
    deleteFiles: '{agent} wants to delete files',
    pushToGithub: '{agent} wants to push to GitHub',
    discardChanges: '{agent} wants to discard local changes',
    changePermissions: '{agent} wants to change file permissions',
    changeOwnership: '{agent} wants to change file ownership',
    runAsAdmin: '{agent} wants to run a command as administrator',
    downloadFromHost: '{agent} wants to download from {host}',
    downloadFromInternet: '{agent} wants to download from the internet',
    installPackage: '{agent} wants to install {package}',
    stopProcess: '{agent} wants to stop a running process',
    runCommand: '{agent} wants to run a command',
    modifyFileNamed: '{agent} wants to modify {name}',
    modifyFile: '{agent} wants to modify a file',
    fetchFromHost: '{agent} wants to fetch from {host}',
    fetchFromInternet: '{agent} wants to fetch from the internet',
    searchWeb: '{agent} wants to search the web',
    finishedPlanning: '{agent} finished planning and wants to start',
    sensitiveAction: '{agent} wants to run a sensitive action',
    // Bodies
    insideDir: 'Inside {dir}.',
    pushDetails: 'Push {branch} to {remote}.',
    hardResetDetails: 'Hard-reset to {ref}. Any uncommitted edits will be lost.',
    forceCheckoutDetails: 'Force-checkout — any uncommitted edits will be lost.',
    sudoDetails: 'sudo elevates privileges system-wide.',
    usingTool: 'Using {tool}.',
    openDevDetails: 'Open developer details to see the exact command.',
    commandStartsWith: 'Starts with {verb}. Open developer details to see the exact command.',
    planToAgentBody: 'Approve to switch from Plan mode to Agent mode for the rest of this turn.',
    toolLabel: 'Tool: {tool}. Open developer details to see the exact request.',
    showRawDetail: 'Show what Claude is about to run',
    askingPermission: 'Claude is asking for permission',
    addsGlobalRule: 'Adds a global rule: {rule}',
    couldNotSave: 'Could not save your decision',
    requestGone: 'This request was already resolved.',
    couldNotSaveDetail: 'Could not save your decision: {error}',
    allowAlwaysFor: 'Allow always for',
  },

  // ── Error boundary ─────────────────────────────────────────────────
  errorBoundary: {
    title: 'Something broke',
    body: 'The dashboard hit an unexpected error and stopped rendering. Your agents on the server are unaffected — this is only the browser view.',
    reload: 'Reload',
    copyDetails: 'Copy details',
    crashReport: 'As Oasis crash report',
  },

  // ── Protected route ────────────────────────────────────────────────
  protectedRoute: {
    loading: 'Loading dashboard…',
  },

  // ── Autocomplete dropdown ──────────────────────────────────────────
  autocomplete: {
    noResults: 'No results',
    loading: 'Searching…',
    error: 'Could not load suggestions',
  },

  // Campaign agent-noise-collapse — collapsed internal-report row.
  internalReport: {
    helperNamed: 'Internal report from {name}',
    helperAnonymous: 'Internal report from a helper',
    system: 'System notice',
  },

  // ── Shared / common ────────────────────────────────────────────────
  common: {
    cancel: 'Cancel',
    retry: 'Retry',
    close: 'Close',
    save: 'Save',
    edit: 'Edit',
    resend: 'Resend',
    searchNoMatch: 'No agents match "{query}"',
    refresh: 'Refresh',
  },

  // ── Tooltips ───────────────────────────────────────────────────────
  tooltip: {
    newAgent: 'New agent',
    searchAgents: 'Search agents',
    archive: 'Archive',
    restore: 'Restore — move back to active',
    deletePermanently: 'Delete permanently',
    moreActions: 'More actions',
    stopAgent: 'Stop agent',
    notifications: 'Notifications',
    settings: 'Settings',
    account: 'Account',
  },

  // ── Sticky / user message ──────────────────────────────────────────
  message: {
    edit: 'Edit',
    resend: 'Resend',
    stamp: { today: 'Today', yesterday: 'Yesterday' },
    // Sprint agent-message-attribution (D3) — origin chip labels.
    origin: {
      system: 'System',
      wakeup: 'Wake-up',
      // Fallback label when the sending agent is not in the live store
      // (hard-deleted/internal) — `id` is the 8-char short id (D4).
      agentFallback: 'Agent {id}',
    },
    // Sprint machine-message-collapse (D3, D8) — hover hint on the collapsed
    // marker's trigger; the text itself is only a click away.
    collapsed: {
      hint: 'Click to read the message',
    },
  },

  // ── Scroll to bottom ───────────────────────────────────────────────
  scrollToBottom: {
    label: 'Scroll to bottom',
    newMessage: '{count} new message',
    newMessages: '{count} new messages',
    ariaLabel: '{count} new messages, scroll to bottom',
  },

  // ── Founder message rail (sprint chatgpt-message-rail, D10) ─────────
  messageRail: {
    label: 'Your messages index',
    listTitle: 'Your messages',
    attachmentOnly: '[attachment]',
    current: 'You are here',
  },

  // ── Chat view ─────────────────────────────────────────────────────
  chatView: {
    loadingMessages: 'Loading messages…',
    couldNotLoadMessages: 'Could not load messages: {error}',
    noMessagesYet: 'No messages yet — the agent will reply shortly.',
    loadingOlder: 'Loading older messages…',
  },

  // ── Conversation panel (sprint fc-conversation-panel, §2) ──────────
  conversationPanel: {
    title: 'Conversation details',
    expand: 'Show the conversation panel',
    collapse: 'Hide the conversation panel',
    campaign: {
      title: 'Campaign, sprint & phase',
      body: 'Nothing to pull yet. The aos-harness-runtime campaign will bring it — it will keep track of which campaign, sprint and phase each conversation is in.',
    },
    documents: {
      title: 'Documents & links',
      loading: 'Loading…',
      partialWindow: 'From the stretch you have loaded. Scroll up in the conversation to include earlier turns.',
      docsLabel: 'Documents',
      linksLabel: 'Links',
      noneYet: 'Nothing yet.',
      more: '+{count} more',
    },
    progress: {
      title: 'Progress',
      noPlanYet: 'This agent has not written a plan yet.',
      readout: '{current} of {total}',
    },
    summary: {
      title: 'Summary',
      body: 'Nothing to pull yet. The aos-observability campaign will bring it — it will store and summarize conversations.',
    },
  },

  // ── Conversation search (sprint fc-conversation-search, §2) ────────
  // Corpus is `user` + `assistant_text` only (§2.1) — "what was said", never
  // tool activity. No backend route (§10.2); the fleet-wide half is named,
  // not built (§2.5, owner campaign aos-observability — a proper name,
  // never translated, comms.md §Proper names).
  conversationSearch: {
    trigger: 'Search this conversation',
    close: 'Close search',
    previous: 'Previous result',
    next: 'Next result',
    matchCount: '{current} of {total}',
    scopeHint: 'Search what you and the agent have written.',
    scopePartial:
      'Searched the {count} messages of this conversation you already have loaded. There is still earlier conversation not loaded.',
    loadOlder: 'Load 200 earlier messages and search again',
    loadOlderFailed: 'Could not load earlier messages. Try again.',
    noResults: 'No results in what is loaded.',
    fleetWidePending: 'Searching across all conversations arrives with the aos-observability campaign.',
    moreResults: '+{count} more',
    loading: 'Loading the conversation…',
    loadError: 'Could not load the conversation.',
    authorYou: 'You',
  },

  // ── Message bubble ────────────────────────────────────────────────
  messageBubble: {
    empty: '(empty)',
    toolError: 'Tool error',
    toolResult: 'Tool result',
  },

  // ── Partial indicator ─────────────────────────────────────────────
  partialIndicator: {
    ariaLabel: 'Agent is typing',
  },

  // Sprint 20.3 PR-7 (D013-EC-22) — live-stream truncated pill.
  streamTruncated: {
    label: '[truncated · reload to refetch]',
    ariaLabel: 'Live stream truncated — click to reload from server',
    icon: '⚠',
  },

  // ── Search bar ────────────────────────────────────────────────────
  searchBar: {
    placeholder: 'Search agents…',
    ariaLabel: 'Search agents',
    clearAriaLabel: 'Clear search',
  },

  // ── Spinner ───────────────────────────────────────────────────────
  spinner: {
    ariaLabel: 'Loading',
  },

  // ── Agent context menu extra ──────────────────────────────────────
  agentActions: {
    ariaLabel: 'Agent actions',
  },

  // ── Permission card extra ─────────────────────────────────────────
  permissionCard: {
    ariaLabel: 'Agent is asking for permission',
    showDevDetails: 'Show what the agent is about to run',
  },

  // ── Aggregation card ──────────────────────────────────────────────────
  aggregation: {
    running: 'running',
    errored: '{count} errored',
    completed: 'completed',
  },

  // ── Turn summary card ─────────────────────────────────────────────────
  turnSummary: {
    errored: 'Errored',
    stopped: 'Stopped',
    typecheck: 'Type check',
    tests: 'Tests',
    lint: 'Lint',
    build: 'Build',
    failed: 'failed',
    // Sprint turn-verdict-line (D3) — metric translation, singular/plural
    // pairs (see es.ts for the rationale, shared verbatim).
    metricFailedOne: '1 failed',
    metricFailedMany: '{count} failed',
    metricPassedOne: '1 passed',
    metricPassedMany: '{count} passed',
    metricErrorsOne: '1 error',
    metricErrorsMany: '{count} errors',
    metricWarningsOne: '1 warning',
    metricWarningsMany: '{count} warnings',
    // Sprint turn-end-signals-line (D2, TES2) — see es.ts for rationale.
    blockedOne: '1 action blocked',
    blockedMany: '{count} actions blocked',
  },

  // ── Compact action rows ───────────────────────────────────────────────
  actionRow: {
    durationSeconds: '{s}s',
    durationMinutes: '{m}m {s}s',
    durationHours: '{h}h {m}m',
    countActions: '{count} actions',
    failedOne: '1 failed',
    failedMany: '{count} failed',
  },

  // ── Collapsed turn header ─────────────────────────────────────────────
  turnCollapse: {
    worked: 'Worked',
    workedForSeconds: 'Worked for {s}s',
    workedForMinutes: 'Worked for {m}m {s}s',
    workedForHours: 'Worked for {h}h {m}m',
  },

  // ── Work segment header (sprint work-segment-collapse, D3) ─────────────
  // Composition over `formatWorkedFor`'s output — no second verb, no
  // second formatter (D3 "header chrome is shared, not duplicated").
  workSegment: {
    header: '{worked} · {time}',
    ariaLabel: 'Work done between two messages',
    // Sprint subagent-launch-ack-liveness (D6) — a background launch inside the segment with
    // no completion signal yet: the header says SINCE, never "Worked for" (that would assert a
    // finished duration this segment doesn't have).
    headerLive: 'Working since {time}',
  },

  // ── Sticky progress bar ──────────────────────────────────────────────
  progress: {
    allDone: 'All steps done',
    working: 'Working…',
    phaseOf: 'Phase {current} of {total}',
  },

  // ── Todo update pill ─────────────────────────────────────────────────
  todoUpdate: {
    started: 'Started',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },

  // ── Ask user question card ───────────────────────────────────────────
  askQuestion: {
    heading: 'Claude needs your input',
    pickOption: 'Pick an option below to continue.',
    answerQuestions: 'Answer {count} questions to continue.',
    submit: 'Submit',
    pickAny: '(pick any)',
    noOptions: 'No options provided.',
    askingQuestion: 'Claude is asking a question',
    couldNotSend: 'Could not send your answer',
    alreadyAnswered: 'This question was already answered.',
    couldNotSendDetail: 'Could not send your answer: {error}',
  },

  // ── Permission outcome pill ──────────────────────────────────────────
  permissionOutcome: {
    autoAllowed: 'Auto-allowed:',
    allowed: 'Allowed:',
    denied: 'Denied:',
    // D013-EC-197 (sprint e97-permission-window) D5/D6.
    expired: 'Timed out:',
    expiredAllowAlwaysHint:
      'The tool already ran without your response. Allow always applies to the next time.',
    decidedBy: 'Decided by:',
    reason: 'Reason:',
    allowlistPattern: 'Allowlist pattern:',
    tool: 'Tool:',
  },

  // ── Agent list item ──────────────────────────────────────────────────
  agentList: {
    working: 'Working…',
    // Sprint dispatch-lineage-ui (D2) — badge of a dispatched session
    // (reachable only through an explicit search).
    dispatchedBy: 'Dispatched by {name}',
    waitingForInput: 'Waiting for input',
    noActivity: 'No activity in {min}m',
    done: 'Done',
    diffStat: '+{added} / -{removed} · {files} files',
    // Sprint UI-7b — variant used when linesRemoved is 0; we drop the
    // "-0" segment so the sidebar row doesn't carry a meaningless zero.
    diffStatNoRemoved: '+{added} · {files} files',
    unread: 'Unread',
    renameAgent: 'Rename agent',
  },

  // ── Mode chip ────────────────────────────────────────────────────────
  modeChip: {
    agent: 'Autonomous',
    plan: 'Plan',
    auto: 'Autonomous',
    agentDesc: 'Runs without asking. Dangerous operations are blocked automatically.',
    planDesc: 'Read-only — explores and plans without changing files',
    autoDesc: 'Runs without asking. Dangerous operations are blocked automatically.',
    modeAriaLabel: 'Permission mode: {mode}. Click to change.',
  },

  // ── Model/effort chips ───────────────────────────────────────────────
  modelChip: {
    modelAndEffortAriaLabel: 'Model: {model}, effort: {effort}. Click to change.',
    popoverAriaLabel: 'Pick model and effort',
    opusOnlyTag: 'Opus only',
  },

  // ── Branch chip ──────────────────────────────────────────────────────
  branch: {
    ahead: '{count} ahead',
    behind: '{count} behind',
    // Sprint UI-7b (D76) — ahead/behind relative to origin/main (distinct
    // from the branch's own upstream sync). Used by AheadBehindChip.
    aheadOfMain: '{count} ahead of main',
    behindOfMain: '{count} behind main',
    uncommittedChanges: 'uncommitted changes',
  },

  // ── Runner chip (Sprint 20.3 PR-9 / D20 / UC-38) ──────────────────────
  // Tooltip strings — Cursor-parity calm and structured, no emoji.
  // Order: `Runner-N · acct-id · cap NN%`.
  runnerChip: {
    tooltipFull: '{hostname} · {account} · {cap}',
    tooltipNoCap: '{hostname} · {account}',
    tooltipMinimal: '{runner} · usage not yet reported',
    cap: 'used {value}%',
    accountUnknown: 'account not reported',
  },

  // ── Context donut ────────────────────────────────────────────────────
  contextDonut: {
    ariaLabel: 'Context used {pct}%',
    defaultTooltip: 'Context used: {pct}%\nMeasured from the model\'s most recent response.',
    // Sprint compaction-visibility (D14) — used when Composer.tsx knows
    // the real token counts (`agent.contextUsedTokens`/`contextWindowTokens`);
    // otherwise (incl. right after a compact_boundary, D8) falls back to
    // the generic `defaultTooltip` above.
    tooltipWithTokens: 'Context used: {used} / {max} tokens ({pct}%)\nMeasured from the model\'s most recent response.',
  },

  // ── Compaction divider (Sprint compaction-visibility, D11/D13) ────────
  // The popover BODY (rendered by CompactionDivider.tsx, not a key here)
  // is the CLI's own summary text, English verbatim regardless of locale —
  // a declared exception to founder-alignment.md §Communication (D11, R1
  // change 7): the summary is literal CLI evidence, not As Oasis copy.
  compaction: {
    dividerLabel: 'Conversation compacted · ~{freed} tokens freed',
    popoverTitle: 'What the agent remembers from before',
    popoverMetaAuto: 'Automatic compaction · {pre} → {post} tokens · {duration}',
    popoverMetaManual: 'Manual compaction · {pre} → {post} tokens · {duration}',
    // Unknown/future trigger (D2: trigger passes through as an opaque
    // string; the UI degrades silently on anything but auto/manual).
    popoverMeta: 'Compaction · {pre} → {post} tokens · {duration}',
    noSummary: 'No summary was saved for this compaction.',
  },

  // ── Runners-health pill (sidebar header) ─────────────────────────────
  // Sprint UI compaction (2026-05-11) — replaces the old `sidebar.heading`
  // ("Agents") + the vestigial `ServerSelector` chip. The pill is the
  // sidebar header's only label now. See `RunnersHealthPill.tsx`.
  // ── Project provisioning pill (Sprint add-project-autoprovision) ──
  // Failure copy is owned by the backend (founderCopyForProvisionFailure
  // in projects/provision-bridge.ts) and rendered verbatim; only the
  // in-progress label and the defensive fallback live here.
  projectProvisioning: {
    preparing: 'Preparing project…',
    failedFallback: 'Project setup failed. The first agent will retry automatically.',
  },

  runnersHealth: {
    ariaLabel: 'Runner status — click to see capacity details',
    // Plural-count label, all online + cap healthy:
    //   "2 runners"           (without cap segment)
    //   "2 runners · 78% free" (allHealthyWithCap)
    allHealthy: '{count} runners',
    allHealthyWithCap: '{count} runners · {cap}',
    // Singular variant
    singleRunner: '1 runner',
    singleRunnerWithCap: '1 runner · {cap}',
    // Mixed online/offline state
    degraded: '{online}/{total} runners',
    // Zero online — cannot spawn anything
    allOffline: 'No runners online',
    // Cap fragment substituted into the labels above
    cap: {
      percent: '{value}% free',
    },
    tooltip: {
      title: 'Runner capacity',
      // Row when the runner is online and reports cap
      capFree: '{value}% free',
      // Row when the runner is online but has not sent a cap-bearing
      // heartbeat yet (newly provisioned, D013-EC-60)
      capUnknown: 'cap unknown',
      // Sprint runner-quarantine-truth (D3, D013-EC-126) — per-runner
      // state words. The flat "unavailable" ×12 gave the founder zero
      // signal during the fleet-quarantine incident. Quarantined rows
      // additionally humanize the reason (`reason` group below).
      stateOffline: 'disconnected',
      stateDraining: 'draining',
      // Edge case: no runners at all in the store
      empty: 'No runners registered yet.',
    },
    // Founder-mode labels for each `RunnerUnavailableReason`. Never
    // expose the enum value itself (founder-first §5 — no jargon);
    // developer detail lives in Settings → Servers. Vocabulary mirrors
    // the operator alerts in `backend/src/api/runner-ws.ts`
    // (`renderUnavailableAlert`), translated to founder language.
    reason: {
      missingCredential: 'credential check failing',
      claudeAccountBlocked: 'Claude account blocked',
      claudeNotAuthenticated: 'Claude sign-in needed',
      diskFull: 'disk full',
      wrongWorkspace: 'credential setup issue',
      incompatibleVersion: 'needs an update',
    },
  },

  // ── Usage modal ──────────────────────────────────────────────────────
  // Sprint 20.3 PR-10 — repurposed from per-account OAuth aggregate
  // (which only made sense pre-S20.2 single-runner) to per-runner
  // aggregation across the whole topology. D17.
  usage: {
    title: 'Runner usage',
    description: 'Capacity and utilization across every runner in the deployment.',
    loading: 'Fetching usage…',
    error: 'Could not fetch runner usage. Try again in a few minutes.',
    // Sprint usage-last-known-good (D013-EC-172) D2 — opening the modal no
    // longer forces an upstream call while a Retry-After window is armed,
    // so the escape hatch has to be explicit and say what it talks to.
    retryNow: 'Retry now',
    retryNowHint: 'Asks Anthropic right now',
    // D4 — WHICH silence this is. The founder-facing answer to "is
    // Anthropic still limiting us, or are we simply not asking?", obtained
    // without spending an upstream call. Rendered as a muted line UNDER the
    // numbers, never as a card that replaces them.
    lastAttemptNotAsked: 'Not checked until {time} (Anthropic asked us to wait)',
    lastAttemptFailedRetryAt: 'Anthropic limited us when we tried · retry at {time}',
    // Same case, no instant to name (timeout, auth error, parse failure):
    // upstream never told us when to come back.
    lastAttemptFailed: 'Could not update this reading on the last attempt',
    // Sprint usage-from-stream (D4, change 9b) — replaces the last-attempt
    // note for a row whose session/weekly numbers come from the CLI
    // stream while its own usage RPC is dead: the amber error card is
    // gone (numbers ARE showing), so this is the one visible trace of a
    // dead RPC until EC-222's alert ships.
    // Sprint usage-fable-window-from-stream (D4 iv) — never names a
    // Fable bar that may be absent on this row (NB-6): a stream-only row
    // may or may not have a Fable-typed turn yet.
    streamOnlyNote:
      'Readings from the agent stream; Sonnet/Opus unavailable',
    empty: 'No runners registered yet.',
    // Per-runner table column headers
    colRunner: 'Runner',
    colAccount: 'Account',
    colCap: '5h used',
    colWeeklySonnet: 'Sonnet · this week',
    colWeeklyOpus: 'Opus · this week',
    colActive: 'Active',
    colStatus: 'Status',
    colLastSeen: 'Last seen',
    // Status badge labels — one per `RunnerAvailability` state (sprint
    // runner-quarantine-truth D4). The modal previously rendered raw
    // WS `status`, which said "ONLINE" about quarantined runners while
    // the banner said "offline" about the same fleet (D013-EC-126).
    statusOnline: 'Online',
    statusOffline: 'Offline',
    statusDraining: 'Draining',
    statusQuarantined: 'Quarantined',
    // Footer
    footerEarliestReset: 'Earliest reset: {hostname} in {time}',
    footerNoReset: 'No cap reset reported yet.',
    // Sprint runner-claude-version-visibility — per-runner Claude Code
    // version + a badge when the runner is behind the fleet maximum.
    claudeVersionLabel: 'Claude Code',
    versionOutdated: 'outdated',
    // Sprint usage-single-emitter (D013-EC-166) D3 — the data's age,
    // shown on every usage surface. After this sprint most reads come
    // from the control-plane cache on purpose, so the founder has to be
    // able to tell a live reading from an old one. Past 30 min the copy
    // stops implying freshness and says so.
    ageJustNow: 'Updated just now',
    ageMinutes: 'Updated {age} ago',
    ageStale: 'Updated {age} ago — this may be out of date',
    ageUnknown: 'No usage reading yet',
    // Sprint usage-from-stream (D4) — short fallback for
    // `noReadingSinceReset`'s `{age}` slot when no per-bucket stamp is
    // known (a bucket with no per-window provenance, e.g. `extra_usage`
    // — RPC-only forever). Deliberately NOT `ageUnknown` above: that is
    // a full sentence and does not fit "last one {age} ago" grammar.
    ageUnknownShort: 'an unknown time',
    // Sprint usage-from-stream (D4) — a bucket whose window has ROTATED
    // with no new reading since. Render-only: never a numeric 0% (the
    // founder's stated fear runs the other way — a stale reading can
    // only OVERSTATE usage, never understate it, but a window reset
    // makes the truth genuinely unknown until the next agent turn).
    noReadingSinceReset: 'Window reset — no readings since (last one {age} ago)',
    // Per-bar caption next to the percentage and below the bar
    percent: '{value}%',
    resetsIn: 'Resets in {time}',
    // Cell fallback
    capUnknown: '—',
    hoursOfLimit: '{value}h / {limit}h',
    // Sprint 20.8 (D013-EC-76) — bar labels for the five canonical
    // Anthropic buckets. Same wording Claude Code uses in its own
    // Account & Usage panel (Cursor parity per cursor-ux-mirror.md).
    session5h: 'Session (5h)',
    weekly7d: 'Weekly (7d)',
    // Sprint usage-fable-window-from-stream (D4) — the stream-sourced
    // "Fable" weekly window (`seven_day_overage_included`). Label says
    // "Fable"; the wire key stays the vendor's (spec §1/F3).
    weeklyFable: 'Weekly · Fable',
    weeklySonnet: 'Sonnet weekly',
    weeklyOpus: 'Opus weekly',
    extraUsage: 'Extra usage',
    // Sprint usage-limits-v2 (2026-07-08) — labels for the canonical
    // `limits[]` array Anthropic now surfaces. `limitWeeklyScopedModel`
    // is used whenever the entry carries a per-model display name
    // (e.g. "Fable"); the un-scoped fallback covers the rare case where
    // `weekly_scoped` arrives without a model name.
    limitSession: 'Session (5h)',
    limitWeeklyAll: 'Weekly (7d)',
    limitWeeklyScoped: 'Weekly · scoped',
    limitWeeklyScopedModel: 'Weekly · {model}',
    // Red-state sub-label when a limit is active or has hit 100 %.
    limitReached: 'Limit reached — resets in {time}',
    // Per-runner failure copy (founder-mode). Each maps to a
    // `usageErrorCode` returned by the bridge. Keep these declarative
    // + actionable: every message says what happened and what to do
    // next.
    errorOauthMissing: 'Claude credentials not found on {hostname}. Run `claude login` on the runner.',
    errorOauthInvalid: 'Claude token expired on {hostname}. Run `claude login` on the runner.',
    errorRateLimited: 'Anthropic rate-limited the usage endpoint for {hostname}. Try again in a few minutes.',
    // Sprint usage-backoff-truth (D013-EC-148) — used instead of the line
    // above whenever we know when the next attempt happens.
    errorRateLimitedRetryAt: 'Anthropic limited {hostname}. Automatic retry at {time}.',
    errorUpstream: 'Could not fetch usage from {hostname}. Try again in a moment.',
    errorTimeout: '{hostname} did not reply in time. The runner is likely overloaded — try again shortly.',
    errorRunnerOffline: 'Runner offline since {lastSeen}. Reconnect to see usage.',
  },

  // ── Tool call block ──────────────────────────────────────────────────
  toolCall: {
    // Sprint orphan-tool-rows-honest (D013-EC-147) — quiet terminal label
    // for a tool whose result never arrived (process died mid-tool).
    interrupted: 'Interrupted',
    running: '(running…)',
    error: '(error)',
    args: 'Args',
    errorLabel: 'Error',
    result: 'Result',
    empty: '(empty)',
    showLess: 'Show less',
    showMore: 'Show more ({chars} chars)',
    inputs: 'Inputs',
    // Sprint monitor-tool-live-timer — inline elapsed counter for tool
    // calls still in flight (used by Monitor / any tool that exceeds the
    // 2 s threshold). Format mirrors Cursor's "Monitored background tasks
    // · Waiting Xs" pattern.
    waitingSeconds: 'Waiting {s}s',
    waitingMinutes: 'Waiting {m}m {s}s',
    waitingHours: 'Waiting {h}h {m}m',
  },

  // ── Doc viewer (D-012, sprint md-doc-viewer; D-019 download/binary) ─
  doc: {
    loading: 'Loading…',
    notFound: '{path} no longer exists in this project.',
    tooLarge: 'This doc is larger than 25 MB.',
    forbidden: 'Path is not allowed.',
    sessionExpired: 'Session expired. Please reload.',
    networkError: 'Could not load this doc. Check your connection.',
    retry: 'Retry',
    close: 'Close',
    download: 'Download',
    noWorkdir: 'This agent has no working directory yet.',
    pdfIosFallback: 'Preview not available on iOS. Tap Download to open this PDF.',
  },

  // ── AI Elements rendering surface (sprint ai-elements-rendering) ─
  aiElements: {
    chainOfThought: {
      header: 'Thinking steps',
    },
    reasoning: {
      thinking: 'Thinking…',
      thoughtBriefly: 'Thought for a few seconds',
      thoughtForSeconds: 'Thought for {duration} seconds',
    },
    codeBlock: {
      copy: 'Copy',
      copied: 'Copied',
      copyAria: 'Copy code to clipboard',
      copiedAria: 'Code copied to clipboard',
    },
    attachments: {
      image: 'Image',
      file: 'Attachment',
      remove: 'Remove attachment',
      uploadFiles: 'Upload files',
      addPhotosOrFiles: 'Add photos or files',
      errorAccept: 'No files match the accepted types.',
      errorMaxFileSize: 'All files exceed the maximum size.',
      errorMaxFiles: 'Too many files. Some were not added.',
    },
  },

  // ── DS.4 mockup stories (campaign aos-design-system, sprint
  // aos-ds-skin-asoasis) — Storybook-only, never shown in the real app.
  // Kept in i18n anyway (design-discipline's own convention: every mockup
  // story uses real components, and i18n-enforce/no-literal-strings-in-jsx
  // has no __mockups__ exemption).
  ds4Mockup: {
    notificationsTooltip: '3 agents need your attention',
    conversationOutOfScope: '(conversation — out of scope for this mockup)',
    composer: {
      history1: "I've finished reviewing PR #482. All green except a lint warning in the settings module.",
      history2: 'OK, fix it and move on to the next item on the plan.',
      history3: "Done. I'm now running the full backend test suite before continuing.",
    },
    settings: {
      title: 'Settings',
      description: 'Workspace preferences',
      cancel: 'Cancel',
      save: 'Save',
      telegramNotifications: 'Telegram notifications',
      autoUpdate: 'Update automatically',
      devDetails: 'Show developer details',
      dangerZone: 'Danger zone',
      dangerZoneHint: 'Deleting this workspace is irreversible.',
      deleteWorkspace: 'Delete workspace',
    },
  },
} as const;

// Widen literal string types to `string` so es.ts can hold different
// values while enforcing identical key structure.
type DeepStringify<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : DeepStringify<T[K]>;
};

export type Translations = DeepStringify<typeof en>;
export default en;
